import { BlockCard } from "../primitives";
import { BlockBaseProps, Delta, Option } from "../types";
import MetricMenu from "./MetricMenu";
import ReadingTrendsBlockSkeleton from "./Skeleton";
import TrendChart, { TrendSeries } from "./TrendChart";
import styles from "./style.module.scss";

export interface TrendLegendItem {
  key: string;
  label: string;
  color: string;
  /** Already formatted total for the active range. */
  total: string;
  delta?: Delta;
}

interface ReadingTrendsBlockProps extends BlockBaseProps {
  series: TrendSeries[];
  labels: string[];
  legend: TrendLegendItem[];
  metrics: Option[];
  metric: string;
  onMetricChange: (key: string) => void;
  menuOpen?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
  title?: string;
}

/** Reading volume over time for one chosen metric. */
function ReadingTrendsBlock({
  series,
  labels,
  legend,
  metrics,
  metric,
  onMetricChange,
  menuOpen = false,
  onMenuOpenChange,
  title = "Reading growth trends",
  loading,
  empty,
  className,
}: ReadingTrendsBlockProps) {
  if (loading) {
    return <ReadingTrendsBlockSkeleton className={className} />;
  }

  const activeLabel =
    metrics.find((option) => option.key === metric)?.label ?? "Select metric";

  return (
    <BlockCard
      empty={empty}
      title={title}
      headerTight
      className={className}
      actions={
        <MetricMenu
          options={metrics}
          value={metric}
          onChange={onMetricChange}
          open={menuOpen}
          onToggleOpen={(open) => onMenuOpenChange?.(open)}
          label={activeLabel}
        />
      }
    >
      <div className={styles.legend}>
        {legend.map((item) => (
          <div key={item.key} className={styles.legendItem}>
            <span className={styles.legendLine} style={{ background: item.color }} />
            {item.label}
            <b className={styles.legendTotal}>{item.total}</b>
            {item.delta && (
              <span className={styles.legendDelta} data-tone={item.delta.tone}>
                {item.delta.value}
              </span>
            )}
          </div>
        ))}
      </div>

      <TrendChart series={series} labels={labels} />
    </BlockCard>
  );
}

export default ReadingTrendsBlock;
